import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Pause, Play } from "lucide-react";
import { cn } from "@/lib/utils";

interface LaunchSettingsStepProps {
  data: {
    multi_advertiser_opt_in: boolean | null;
    default_ad_status: string | null;
  };
  onChange: (data: Partial<{
    multi_advertiser_opt_in: boolean;
    default_ad_status: string;
  }>) => void;
}

const STATUS_OPTIONS = [
  {
    value: "PAUSED",
    label: "Paused",
    subtitle: "Ads are created but won't run until you manually activate them.",
    icon: Pause,
  },
  {
    value: "ACTIVE",
    label: "Active",
    subtitle: "Ads start running immediately after creation.",
    icon: Play,
  },
] as const;

export function LaunchSettingsStep({ data, onChange }: LaunchSettingsStepProps) {
  const multiAdvertiser = data.multi_advertiser_opt_in ?? true;
  const adStatus = data.default_ad_status ?? "PAUSED";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Launch Settings</CardTitle>
        <CardDescription>Configure default settings for ad creation</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Multi-Advertiser */}
        <div className="rounded-lg border border-border p-4 flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label className="text-sm font-medium">Multi-Advertiser Ads</Label>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Allow Meta to show your ad alongside ads from other businesses to help people discover products and services.
            </p>
          </div>
          <Switch
            checked={multiAdvertiser}
            onCheckedChange={(val) => onChange({ multi_advertiser_opt_in: val })}
          />
        </div>

        {/* Default Ad Status */}
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-sm font-medium">Default Ad Launch Status</Label>
            <p className="text-xs text-muted-foreground">Choose the default status for newly created ads.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {STATUS_OPTIONS.map((opt) => {
              const isSelected = adStatus === opt.value;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onChange({ default_ad_status: opt.value })}
                  className={cn(
                    "rounded-lg border p-4 text-left transition-all",
                    isSelected
                      ? "border-primary ring-2 ring-primary/30 bg-primary/5"
                      : "border-border bg-card hover:bg-accent"
                  )}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={cn("h-4 w-4", isSelected ? "text-primary" : "text-muted-foreground")} />
                    <span className={cn("text-sm font-medium", isSelected ? "text-foreground" : "text-muted-foreground")}>
                      {opt.label}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{opt.subtitle}</p>
                </button>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
